import { useEffect, useState, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import {
  ArrowLeft,
  ArrowRight,
  BookOpen,
  Check,
  CheckCircle2,
  Cloud,
  Code2,
  Database,
  ExternalLink,
  Layers3,
  LayoutTemplate,
  Loader2,
  Mail,
  Palette,
  Plus,
  ShieldCheck,
  Sparkles,
  Users,
  X,
} from "lucide-react";
import { unwrap } from "@tern/api-client";
import { DatabaseConnectionFields, emptyDatabaseInput, databaseInputComplete, databaseInputPayload } from "@/components/DatabaseConnectionFields";
import { TernMark } from "@/components/TernMark";
import { errorMessage, logout, meQuery } from "@/lib/api";
import { instancePath } from "@/lib/instance";
import {
  accountApi,
  completeOnboarding,
  createTeam,
  lookupTeams,
  onboardingQuery,
  requestToJoin,
  saveJobRole,
  type Team,
} from "@/lib/onboarding";

type Step =
  | "choice"
  | "details"
  | "storage"
  | "invite"
  | "join"
  | "role"
  | "welcome";
const roles = [
  "planner",
  "developer",
  "designer",
  "pm",
  "marketing",
  "operations",
  "leadership",
  "other",
] as const;
const roleIcons = [
  LayoutTemplate,
  Code2,
  Palette,
  Layers3,
  Sparkles,
  Database,
  Users,
  Plus,
];

export function OnboardingFrame({
  children,
  step,
  email,
}: {
  children: ReactNode;
  step?: number;
  email?: string;
}) {
  const { t } = useTranslation();
  const signOut = useMutation({
    mutationFn: logout,
    onSuccess: () => {
      location.href = "/login";
    },
  });
  return (
    <div className="onboarding-shell">
      <header className="onboarding-header">
        <a href="/" className="onboarding-brand">
          <TernMark className="h-7 w-7" />
          <span>Tern</span>
        </a>
        {email && (
          <div className="flex items-center gap-4 text-[12px] text-muted">
            <span>{email}</span>
            <button
              className="hover:text-fg"
              onClick={() => signOut.mutate()}
              disabled={signOut.isPending}
            >
              {t("nav.signOut")}
            </button>
          </div>
        )}
      </header>
      {step !== undefined && (
        <nav
          className="onboarding-progress"
          aria-label={t("onboarding.progress")}
        >
          {["team", "storage", "invite", "you"].map((key, index) => (
            <div
              key={key}
              aria-current={step === index ? "step" : undefined}
              className={step >= index ? "is-current" : ""}
            >
              <span>{step > index ? <Check size={12} /> : index + 1}</span>
              {t(`onboarding.steps.${key}`)}
            </div>
          ))}
        </nav>
      )}
      <main className="onboarding-main">{children}</main>
      <footer className="onboarding-footer">
        A little more clarity. A lot more possibility.
      </footer>
    </div>
  );
}

export function NewWorkspacePage() {
  const { t } = useTranslation();
  const me = useQuery(meQuery);
  const qc = useQueryClient();
  const data = useQuery({
    ...onboardingQuery,
    enabled: !instancePath,
    refetchInterval: 5000,
  });
  const initialJoin = new URLSearchParams(location.search).get("join");
  const [step, setStep] = useState<Step | null>(initialJoin ? "join" : null);
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [slugTouched, setSlugTouched] = useState(false);
  const [external, setExternal] = useState(false);
  const [database, setDatabase] = useState(emptyDatabaseInput);
  const [inviteValue, setInviteValue] = useState("");
  const [inviteEmails, setInviteEmails] = useState<string[]>([]);
  const [inviteError, setInviteError] = useState(false);
  const [query, setQuery] = useState(initialJoin ?? "");
  const [selected, setSelected] = useState<Team | null>(null);
  const [jobRole, setJobRole] = useState("");
  const [checkedSlug, setCheckedSlug] = useState("");
  useEffect(() => {
    if (instancePath) location.replace("/new");
  }, []);
  useEffect(() => {
    const timer = setTimeout(() => setCheckedSlug(slug), 350);
    return () => clearTimeout(timer);
  }, [slug]);
  const available = useQuery({
    queryKey: ["team-url", checkedSlug],
    enabled: /^[a-z0-9]{2,32}$/.test(checkedSlug),
    queryFn: async () =>
      unwrap(
        await accountApi.GET("/api/onboarding/availability/{slug}", {
          params: { path: { slug: checkedSlug } },
        }),
      ),
    retry: false,
  });
  const refresh = () =>
    qc.invalidateQueries({ queryKey: onboardingQuery.queryKey });
  const databaseTest = useMutation({
    mutationFn: async () =>
      unwrap(
        await accountApi.POST("/api/onboarding/database/test", {
          body: databaseInputPayload(database),
        }),
      ),
  });
  const create = useMutation({
    mutationFn: () =>
      createTeam({
        name: name.trim(),
        slug,
        invites: inviteEmails,
        database: external ? databaseInputPayload(database) : null,
      }),
    onSuccess: async (team) => {
      setDatabase(emptyDatabaseInput());
      setSelected(team);
      await refresh();
      setStep("role");
    },
  });
  const lookup = useMutation({ mutationFn: () => lookupTeams(query) });
  const join = useMutation({
    mutationFn: (team: Team) => requestToJoin(team.id),
    onSuccess: async () => {
      await refresh();
      lookup.reset();
      setStep(null);
    },
  });
  const role = useMutation({
    mutationFn: () => saveJobRole(jobRole),
    onSuccess: async () => {
      await refresh();
      setStep("welcome");
    },
  });
  const finish = useMutation({
    mutationFn: (id: string) => completeOnboarding(id),
    onSuccess: (res) => {
      location.href = res.next_url;
    },
  });
  const pendingTeam =
    selected ??
    data.data?.teams.find(
      (team) => team.id === data.data?.profile.pending_team,
    );
  const current =
    step ??
    (pendingTeam && !data.data?.profile.welcome_completed
      ? data.data?.profile.job_role
        ? "welcome"
        : "role"
      : "choice");
  const addEmails = () => {
    const added = inviteValue
      .split(/[\s,;]+/)
      .filter(Boolean)
      .map((value) => value.trim().toLowerCase());
    if (
      added.some((value) => !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) ||
      new Set([...inviteEmails, ...added]).size > 30
    ) {
      setInviteError(true);
      return false;
    }
    setInviteEmails(
      [...new Set([...inviteEmails, ...added])].filter(
        (value) => value !== me.data?.user.email,
      ),
    );
    setInviteValue("");
    setInviteError(false);
    return true;
  };
  const title = (key: string, subtitle = key) => (
    <div className="onboarding-heading">
      <span className="onboarding-eyebrow">{t("onboarding.eyebrow")}</span>
      <h1>{t(`onboarding.${key}Title`)}</h1>
      <p>{t(`onboarding.${subtitle}Subtitle`)}</p>
    </div>
  );
  const back = (to: Step) => (
    <button
      type="button"
      className="onboarding-back"
      onClick={() => setStep(to)}
      disabled={create.isPending}
    >
      <ArrowLeft size={15} />
      {t("common.back")}
    </button>
  );
  const error = (value: unknown) =>
    value ? (
      <p className="onboarding-error" role="alert">
        {errorMessage(value)}
      </p>
    ) : null;
  const next = (label: string, pending = false, disabled = false) => (
    <button
      className="onboarding-primary"
      type="submit"
      disabled={disabled || pending}
    >
      {pending ? <Loader2 size={16} className="animate-spin" /> : null}
      {label}
      {!pending && <ArrowRight size={16} />}
    </button>
  );

  if (instancePath || data.isPending)
    return (
      <OnboardingFrame email={me.data?.user.email}>
        <div className="onboarding-loading">
          <Loader2 className="animate-spin" size={24} />
          {t("common.loading")}
        </div>
      </OnboardingFrame>
    );
  if (data.isError)
    return (
      <OnboardingFrame email={me.data?.user.email}>
        {error(data.error)}
        <button className="btn" onClick={() => data.refetch()}>
          {t("onboarding.retry")}
        </button>
      </OnboardingFrame>
    );
  return (
    <OnboardingFrame
      email={me.data?.user.email}
      step={
        ["details", "storage", "invite", "role"].includes(current)
          ? ["details", "storage", "invite", "role"].indexOf(current)
          : undefined
      }
    >
      {current === "choice" && (
        <div className="onboarding-choice">
          {title("choice")}
          {(data.data?.teams.length ?? 0) > 0 && (
            <div className="onboarding-teams">
              {data.data?.teams.map((team) => (
                <a key={team.id} href={`/t/${team.slug}`}>
                  <span className="onboarding-team-avatar">{team.name[0]}</span>
                  <span>
                    <strong>{team.name}</strong>
                    <small>
                      {location.host}/t/{team.slug}
                    </small>
                  </span>
                  <ArrowRight size={17} />
                </a>
              ))}
            </div>
          )}
          <div className="onboarding-options">
            <button
              onClick={() => {
                setSelected(null);
                setStep("details");
              }}
            >
              <span className="onboarding-option-icon">
                <Plus size={23} />
              </span>
              <h2>{t("onboarding.createTeam")}</h2>
              <p>{t("onboarding.createHint")}</p>
              <span className="onboarding-option-action">
                {t("onboarding.getStarted")}
                <ArrowRight size={16} />
              </span>
            </button>
            <button onClick={() => setStep("join")}>
              <span className="onboarding-option-icon">
                <Users size={23} />
              </span>
              <h2>{t("onboarding.joinTeam")}</h2>
              <p>{t("onboarding.joinHint")}</p>
              <span className="onboarding-option-action">
                {t("onboarding.findTeam")}
                <ArrowRight size={16} />
              </span>
            </button>
          </div>
          {!!data.data?.requests.length && (
            <div className="onboarding-requests">
              <h3>{t("onboarding.yourRequests")}</h3>
              {data.data.requests.map((request) => (
                <div key={request.id}>
                  <span>
                    {request.team_name}
                    <small>/t/{request.team_slug}</small>
                  </span>
                  <span className={`onboarding-status ${request.status}`}>
                    {t(`onboarding.requestStatus.${request.status}`)}
                  </span>
                  {request.status === "approved" && (
                    <a href={`/t/${request.team_slug}`} className="text-accent">
                      {t("onboarding.enter")} →
                    </a>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      {current === "details" && (
        <div className="onboarding-form-card">
          {title("details")}
          <form
            onSubmit={(event) => {
              event.preventDefault();
              setStep("storage");
            }}
          >
            <label className="onboarding-field">
              {t("onboarding.teamName")}
              <input
                autoFocus
                className="input"
                value={name}
                onChange={(event) => {
                  setName(event.target.value);
                  if (!slugTouched)
                    setSlug(
                      event.target.value
                        .toLowerCase()
                        .replace(/[^a-z0-9]/g, "")
                        .slice(0, 32),
                    );
                }}
                placeholder={t("onboarding.teamNamePlaceholder")}
                required
                maxLength={80}
              />
              <small>{t("onboarding.teamNameHint")}</small>
            </label>
            <label className="onboarding-field">
              {t("onboarding.teamUrl")}
              <div className="onboarding-url">
                <span>{location.host}/t/</span>
                <input
                  required
                  value={slug}
                  onChange={(event) => {
                    setSlugTouched(true);
                    setSlug(event.target.value.toLowerCase());
                  }}
                  maxLength={32}
                  pattern="[a-z0-9]{2,32}"
                  placeholder="yourteam"
                />
              </div>
              <small>{t("onboarding.urlHint")}</small>
            </label>
            {checkedSlug === slug && /^[a-z0-9]{2,32}$/.test(slug) && (
              <div
                className={`onboarding-availability ${available.data?.available ? "valid" : ""}`}
              >
                {available.isFetching ? (
                  t("onboarding.checkingUrl")
                ) : available.isError ? (
                  errorMessage(available.error)
                ) : available.data?.available ? (
                  <>
                    <Check size={14} />
                    {t("onboarding.urlAvailable")}
                  </>
                ) : (
                  t("onboarding.urlTaken")
                )}
              </div>
            )}
            <div className="onboarding-actions">
              {back("choice")}
              {next(
                t("onboarding.continue"),
                false,
                !name.trim() ||
                  !available.data?.available ||
                  checkedSlug !== slug,
              )}
            </div>
          </form>
        </div>
      )}
      {current === "storage" && (
        <div className="onboarding-form-card onboarding-database-card">
          {title("storage")}
          <form
            onSubmit={(event) => {
              event.preventDefault();
              if (external && (!databaseInputComplete(database) || !databaseTest.isSuccess)) return;
              setStep("invite");
            }}
          >
            <div className="onboarding-storage-options">
              <button
                type="button"
                className={!external ? "selected" : ""}
                onClick={() => setExternal(false)}
              >
                <Cloud size={22} />
                <span>
                  <strong>{t("onboarding.hosted")}</strong>
                  <small>{t("onboarding.hostedHint")}</small>
                </span>
                {!external && <CheckCircle2 size={19} />}
              </button>
              <button
                type="button"
                className={external ? "selected" : ""}
                onClick={() => setExternal(true)}
              >
                <Database size={22} />
                <span>
                  <strong>{t("onboarding.external")}</strong>
                  <small>{t("onboarding.externalHint")}</small>
                </span>
                {external && <CheckCircle2 size={19} />}
              </button>
            </div>
            {external && (
              <div className="onboarding-database-fields">
                <a
                  href="/guides/company-database"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="onboarding-database-guide"
                >
                  <BookOpen size={16} />
                  {t("onboarding.databaseGuide")}
                  <ExternalLink size={13} aria-hidden="true" />
                  <span className="sr-only">{t("onboarding.opensNewTab")}</span>
                </a>
                <p className="onboarding-note">
                  {t("onboarding.isolationHint")}
                </p>
                <DatabaseConnectionFields
                  value={database}
                  disabled={databaseTest.isPending || create.isPending}
                  onChange={(value) => {
                    setDatabase(value);
                    databaseTest.reset();
                    create.reset();
                  }}
                />
                <p className="mt-4 text-[12px] leading-5 text-muted">
                  {t("onboarding.connectionHint")}
                </p>
                <button
                  type="button"
                  className="btn mt-3"
                  disabled={!databaseInputComplete(database) || databaseTest.isPending}
                  onClick={() => databaseTest.mutate()}
                >
                  {databaseTest.isPending
                    ? t("onboarding.testing")
                    : t("onboarding.testConnection")}
                </button>
                {databaseTest.isSuccess && (
                  <p className="mt-2 text-[12px] text-success">
                    {t("onboarding.connectionSuccess")}
                  </p>
                )}
                {error(databaseTest.error)}
              </div>
            )}
            <div className="onboarding-actions">
              {back("details")}
              {next(
                t("onboarding.continue"),
                false,
                external && !databaseTest.isSuccess,
              )}
            </div>
          </form>
        </div>
      )}
      {current === "invite" && (
        <div className="onboarding-form-card">
          {title("invite")}
          <form
            onSubmit={(event) => {
              event.preventDefault();
              if (inviteValue.trim()) {
                addEmails();
                return;
              }
              create.mutate();
            }}
          >
            <label className="onboarding-field">
              {t("onboarding.inviteEmails")}
              <div className="flex gap-2">
                <input
                  autoFocus
                  className="input min-w-0 flex-1"
                  value={inviteValue}
                  onChange={(event) => {
                    setInviteValue(event.target.value);
                    setInviteError(false);
                  }}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" && inviteValue.trim()) {
                      event.preventDefault();
                      addEmails();
                    }
                  }}
                  placeholder="colleague@company.com"
                  disabled={create.isPending}
                />
                <button
                  type="button"
                  className="btn"
                  onClick={addEmails}
                  disabled={!inviteValue.trim() || create.isPending}
                >
                  <Plus size={17} />
                  {t("onboarding.add")}
                </button>
              </div>
              <small>{t("onboarding.inviteHint")}</small>
            </label>
            {inviteError && (
              <p role="alert" className="onboarding-error">
                {t("onboarding.invalidEmails")}
              </p>
            )}
            <div className="onboarding-invite-list">
              {inviteEmails.map((email) => (
                <div key={email}>
                  <Mail size={16} />
                  <span>{email}</span>
                  <button
                    type="button"
                    aria-label={`${t("common.remove")} ${email}`}
                    disabled={create.isPending}
                    onClick={() =>
                      setInviteEmails(
                        inviteEmails.filter((value) => value !== email),
                      )
                    }
                  >
                    <X size={15} />
                  </button>
                </div>
              ))}
            </div>
            <div className="onboarding-team-summary">
              <span className="onboarding-team-avatar">{name[0]}</span>
              <div>
                <strong>{name}</strong>
                <small>
                  {location.host}/t/{slug}
                </small>
              </div>
              <span>
                {external
                  ? t("onboarding.externalShort")
                  : t("onboarding.hostedShort")}
              </span>
            </div>
            {error(create.error)}
            {create.isPending && (
              <p className="onboarding-note" role="status">
                {t("onboarding.provisioningHint")}
              </p>
            )}
            <div className="onboarding-actions">
              {back("storage")}
              {next(
                inviteValue.trim()
                  ? t("onboarding.addEmails")
                  : t("onboarding.createTeam"),
                create.isPending,
              )}
            </div>
          </form>
        </div>
      )}
      {current === "join" && (
        <div className="onboarding-form-card">
          {title("join")}
          <form
            onSubmit={(event) => {
              event.preventDefault();
              lookup.mutate();
            }}
          >
            <label className="onboarding-field">
              {t("onboarding.lookupLabel")}
              <input
                className="input"
                autoFocus
                required
                value={query}
                onChange={(event) => {
                  setQuery(event.target.value);
                  lookup.reset();
                }}
                placeholder={t("onboarding.lookupPlaceholder")}
              />
            </label>
            <div className="onboarding-actions">
              {back("choice")}
              {next(t("onboarding.findTeam"), lookup.isPending, !query.trim())}
            </div>
          </form>
          {error(lookup.error)}
          {error(join.error)}
          {lookup.isSuccess && !lookup.data?.length && (
            <p className="onboarding-note">{t("onboarding.teamNotFound")}</p>
          )}
          <div className="onboarding-lookup-results">
            {lookup.data?.map((team) => (
              <div key={team.id}>
                <span className="onboarding-team-avatar">{team.name[0]}</span>
                <div>
                  <strong>{team.name}</strong>
                  <small>/t/{team.slug}</small>
                </div>
                <button
                  className="btn btn-primary"
                  disabled={join.isPending}
                  onClick={() => join.mutate(team)}
                >
                  {t("onboarding.requestJoin")}
                </button>
              </div>
            ))}
          </div>
          <p className="onboarding-note">
            <ShieldCheck size={16} />
            {t("onboarding.approvalHint")}
          </p>
        </div>
      )}
      {current === "role" && pendingTeam && (
        <div className="onboarding-role-card">
          {title("role")}
          <form
            onSubmit={(event) => {
              event.preventDefault();
              role.mutate();
            }}
          >
            <div
              className="onboarding-roles"
              role="radiogroup"
              aria-label={t("onboarding.roleTitle")}
            >
              {roles.map((value, index) => {
                const Icon = roleIcons[index] ?? Plus;
                return (
                  <button
                    key={value}
                    type="button"
                    role="radio"
                    aria-checked={jobRole === value}
                    className={jobRole === value ? "selected" : ""}
                    onClick={() => setJobRole(value)}
                  >
                    <Icon size={20} />
                    <span>{t(`onboarding.roles.${value}`)}</span>
                    {jobRole === value && <Check size={16} />}
                  </button>
                );
              })}
            </div>
            <p className="onboarding-note">{t("onboarding.roleHint")}</p>
            {error(role.error)}
            <div className="onboarding-actions justify-end!">
              {next(t("onboarding.continue"), role.isPending, !jobRole)}
            </div>
          </form>
        </div>
      )}
      {current === "welcome" && pendingTeam && (
        <div className="onboarding-welcome">
          <div className="onboarding-welcome-mark">
            <div className="onboarding-orbit" />
            <div className="onboarding-orbit second" />
            <TernMark className="h-16 w-16" />
          </div>
          <span className="onboarding-eyebrow">YOUR NEXT CHAPTER</span>
          <h1>{t("onboarding.welcomeTitle", { team: pendingTeam.name })}</h1>
          <p>{t("onboarding.welcomeSubtitle")}</p>
          <div className="onboarding-welcome-features">
            <span>
              <CheckCircle2 size={16} />
              {t("onboarding.readyTeam")}
            </span>
            <span>
              <Database size={16} />
              {t("onboarding.readyDb")}
            </span>
            <span>
              <Users size={16} />
              {t("onboarding.readyYou")}
            </span>
          </div>
          {error(finish.error)}
          <button
            className="onboarding-primary"
            onClick={() => finish.mutate(pendingTeam.id)}
            disabled={finish.isPending}
          >
            {finish.isPending ? (
              <Loader2 size={17} className="animate-spin" />
            ) : null}
            {t("onboarding.openTern")}
            <ArrowRight size={17} />
          </button>
        </div>
      )}
    </OnboardingFrame>
  );
}
